/* eslint-disable @typescript-eslint/naming-convention */
import { Clipboard } from '@angular/cdk/clipboard';
import { Component, computed, ElementRef, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

import { cva, VariantProps } from 'class-variance-authority';
import { ToastService } from '~backoffice/app/~shared/services/toast.service';

export const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-[4px] font-normal px-2.5 py-0.5 text-xs transition-colors whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      copyable: {
        true: 'cursor-pointer hover:opacity-80 max-w-full',
        false: ''
      },
      variant: {
        primary: 'bg-primary-100 text-primary-500 hover:!text-primary-500',
        secondary: 'bg-secondary-500 text-white hover:!text-white',
        outline: 'text-foreground border-foreground/20',
        destructive: 'bg-danger-500/20 !text-danger-500',
        destructiveOutline:
          'text-danger-500 border-2 border-danger-500/30 hover:!text-danger-500',
        info: 'bg-info-100/30 text-info-500/80',
        active: 'bg-secondary-300 text-secondary-500',
        inactive: 'bg-danger-100 text-danger-500',
        success:
          'bg-secondary-300 text-secondary-500 hover:!text-secondary-500',
        successLight:
          'bg-secondary-300 text-secondary-500 hover:!text-secondary-500',
        successOutline:
          'text-secondary-500 border-2 border-secondary-200 hover:!text-secondary-500',
        neutral: 'bg-neutral-100 text-neutral-900',
        successFancyOutlined: 'border-2 border-successFancy text-successFancy',
        dangerFancyOutlined: 'border-2 border-dangerFancy text-dangerFancy',
        primaryFancyOutlined: 'border-2 border-primaryFancy text-primaryFancy',
        infoFancyOutlined: 'border-2 border-infoFancy text-infoFancy'
      },
      size: {
        default: 'px-2.5 py-1 text-2xs',
        sm: 'py-1 text-2xs',
        lg: 'px-3 py-1',
        xl: 'px-4 py-3'
      }
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      copyable: false
    }
  }
);

export type BadgeVariants = VariantProps<typeof badgeVariants>;

@Component({
  selector: '[badge], badge',
  standalone: true,
  imports: [MatTooltipModule, MatIconModule],
  template: `
    @if (content() || subtitle()) {
      <div class="leading-tight text-left w-full max-w-full">
        @if (content()) {
          <p class="flex flex-nowrap gap-1.5 items-center">
            <span class="truncate flex-shrink grow-0">{{ content() }}</span>

            @if (copyable()) {
              <mat-icon
                svgIcon="dgi-copy"
                class="shrink-0"
                [style.width.px]="16"
                [style.height.px]="16"
              ></mat-icon>
            }
          </p>
        }

        @if (subtitle()) {
          <div class="text-[11px] opacity-80 line-clamp-1">
            {{ subtitle() }}
          </div>
        }
      </div>
    } @else {
      <ng-content />
    }
  `,
  host: {
    '[class]': 'computedClasses()',
    '[style.white-space]': 'wrap() || subtitle() ? "normal" : null',
    '[style.text-align]': 'wrap() || subtitle() ? "left" : null',
    '[style.align-items]': 'wrap() || subtitle() ? "flex-start" : null',
    '[style.justify-content]': 'wrap() || subtitle() ? "flex-start" : null',
    '[style.flex-direction]': 'subtitle() ? "column" : null',
    '(click)': 'handleClick()'
  },
  hostDirectives: [
    {
      directive: MatTooltip,
      inputs: ['matTooltip: tooltip']
    }
  ]
})
export class BadgeComponent {
  variant = input<BadgeVariants['variant']>('primary');
  size = input<BadgeVariants['size']>('default');
  content = input<string>('');
  subtitle = input<string>('');
  wrap = input<boolean>(false);
  tooltip = input<string>('');
  copyable = input<boolean>(false);
  className = input<string>('');

  private readonly _clipboard = inject(Clipboard);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _toastService = inject(ToastService);

  computedClasses = computed(() => {
    const baseClasses = badgeVariants({
      variant: this.variant(),
      size: this.size(),
      copyable: this.copyable()
    });
    const extraClasses = this.className();
    return extraClasses ? `${baseClasses} ${extraClasses}` : baseClasses;
  });

  handleClick(): void {
    if (!this.copyable()) {
      return;
    }

    const textToCopy = this._getTextContent();
    if (!textToCopy) {
      return;
    }

    const success = this._clipboard.copy(textToCopy);
    if (success) {
      this._toastService.open({
        content: 'Contenu copié avec succès',
        type: 'success'
      });
    } else {
      this._toastService.open({
        content: 'Erreur lors de la copie',
        type: 'error'
      });
    }
  }

  private _getTextContent(): string {
    const contentInput = this.content();
    if (contentInput) {
      return contentInput;
    }

    const element = this._elementRef.nativeElement;
    return element.textContent?.trim() || '';
  }
}
